import Tagged from 'utils/Tagged'
import { ArtboardDetailInfoFragment, ShareInfoFragment } from 'gqlTypes'

export interface Pagination {
  current: number | undefined
  total: number | undefined
}

export interface Style {
  borders?: Border[]
  borderOptions?: BorderOptions
  fills?: Fill[]
  appearance?: Appearance
  text?: TextStyleProps
  shadows?: Shadow[]
  innerShadows?: Shadow[]
  blur?: Blur
  isDirty?: boolean
  originalSharedStyleValues?: {
    borders?: Border[]
    borderOptions?: BorderOptions
    fills?: Fill[]
    appearance?: Appearance
    text?: TextStyleProps
    shadows?: Shadow[]
    innerShadows?: Shadow[]
    blur?: Blur
  }
}

export interface Shadow extends EnablableSection {
  color: Color
  offsetX: number
  offsetY: number
  blurRadius: number
  spread?: number
  appearance?: Appearance
  blendMode?: string
}

export enum BlurType {
  Gaussian = 'Gaussian Blur',
  Motion = 'Motion Blur',
  Zoom = 'Zoom Blur',
  Background = 'Background Blur',
}

export interface BlurPoint {
  x: number
  y: number
}

export interface Blur extends EnablableSection {
  type: 'motion' | 'zoom' | 'background' | 'gaussian'
  center?: BlurPoint
  motionAngle?: number
  radius?: number
  saturation?: number
  blendMode?: string
}

// ColorFormat and UserPreferences should come from our schema once implemented
// in graphql
export enum ColorFormat {
  HEX,
  RGB,
  HSL,
  OBJC_NSCOLOR,
  SWIFT_NSCOLOR,
  OBJC_UICOLOR,
  SWIFT_UICOLOR,
}

export interface ColorVariable {
  id: string
  name: string
  sourceLibraryName?: string
}

export interface UserPreferences {
  colorFormat: ColorFormat
}

// Share used in sidebar components
// Note: used in DetailSidebar and components below
// Note2: Check DetailView to see where fields are forced to be non-nullable
export type ShareInSidebar = Pick<
  ShareInfoFragment,
  'identifier' | 'userCanAccessVersions' | 'subscriptionStatus' | 'name'
> & {
  commentsEnabled: NonNullable<ShareInfoFragment['commentsEnabled']>
  userCanInspect: ShareInfoFragment['userCanInspect']
  versionTypeAvailableToUser: ShareInfoFragment['versionTypeAvailableToUser']
}

// Artboard used in sidebar components
// Note: used in DetailSidebar and components below
// Note2: Check DetailView to see where fields are forced to be non-nullable
export type ArtboardInSidebar = Pick<
  ArtboardDetailInfoFragment,
  'identifier' | 'isPage' | 'subscriptionStatus'
> & {
  permanentArtboardShortId: NonNullable<
    ArtboardDetailInfoFragment['permanentArtboardShortId']
  >
  documentVersionShortId: NonNullable<
    ArtboardDetailInfoFragment['documentVersionShortId']
  >
  revisionIdentifier: NonNullable<
    ArtboardDetailInfoFragment['revisionIdentifier']
  >
}

export interface SymbolData {
  symbolLayer: Layer | null
  symbolData: SymbolMaster | null
  symbolArtboard: {
    __typename: 'Artboard'
    permanentArtboardShortId: string
    isSymbolMaster: boolean
  } | null
  foreignSymbolShareId?: string
  foreignVersionShortId?: string
}

export interface Point {
  x: number
  y: number
}

export interface Size {
  width: number
  height: number
}

export type Rect = Point & Size

export type RectEdge = 'minX' | 'maxX' | 'minY' | 'maxY'

export type HorizontalSide = 'left' | 'right'
export type VerticalSide = 'top' | 'bottom'
export type Side = HorizontalSide | VerticalSide
export type Orientation = 'horizontal' | 'vertical'
export type Axis = 'x' | 'y'

declare const LayerSymbol: unique symbol
export type LayerId = Tagged<typeof LayerSymbol, string>
export type LayerOrder = Tagged<typeof LayerSymbol, number>

declare const SymbolMasterSymbol: unique symbol
export type SymbolMasterId = Tagged<typeof SymbolMasterSymbol, string>

export interface Preset {
  name: string
  width: number
  height: number
}

export const layerTypes = [
  'MSImmutableHotspotLayer',
  'bitmap',
  'group',
  'oval',
  'polygon',
  'rectangle',
  'shapeGroup',
  'shapePath',
  'star',
  'text',
  'triangle',
  'slice',
] as const

const calculatedLayerTypes = [
  // Used for contextual menu icon
  'symbol',

  // These last 2 are used for the overview info in the right sidebar
  'artboard',
  'page',
] as const

export type LayerType = typeof layerTypes[number]

export const selectionTypes = [...layerTypes, ...calculatedLayerTypes] as const
export type SelectionType = typeof selectionTypes[number]

export interface Layer {
  id: LayerId
  exportID?: string
  parent: LayerId | null
  name: string
  frame: Rect
  frameBeforeRotation?: Rect
  originalFrame: Rect
  order: LayerOrder
  type: LayerType
  rotation?: number
  style?: Style
  cornerRadius?: number[]
  smoothCorners?: boolean
  isCombined?: boolean
  attributedString?: TextAttributedString
  symbolMasterID: SymbolMasterId | null
  exportFormats?: ExportFormat[]
  sharedStyleID: string
  isSymbolRoot: boolean
}

export interface SymbolMaster {
  id: SymbolMasterId
  name: string
  isForeign: boolean
  documentID: string | null
  documentName: string
}

export enum VisibleScaleType {
  scale = 0,
  width = 1,
  height = 2,
}

export interface ExportFormat {
  fileFormat: 'PNG' | 'JPG' | 'TIFF' | 'WEBP' | 'PDF' | 'EPS' | 'SVG'
  name: string
  namingScheme: string
  scale: number
  absoluteSize: number
  // Taken from Sketch
  // https://github.com/sketch-hq/Sketch/blob/605f73c551e7389da2932c7bfe431f247dd4fecf/Modules/SketchModel/Source/MSUserVisibleScaleType.h
  visibleScaleType: VisibleScaleType
}

export interface Point {
  x: number
  y: number
}

export interface Appearance {
  opacity: number
  blendMode: string
}

export interface GradientStops {
  color: { red: number; green: number; blue: number; alpha: number }
  position: number
}

export enum GradientType {
  Linear = 'linear',
  Radial = 'radial',
  Angular = 'angular',
}

export interface Gradient {
  stops: GradientStops[]
  from: Point
  to: Point
  type: GradientType
  appearance?: Appearance
}

export interface Color {
  red: number
  green: number
  blue: number
  alpha?: number
  colorVariableID?: string
}

export enum FillType {
  Color = 'color',
  Gradient = 'gradient',
  Pattern = 'pattern',
}

export enum ImageFillType {
  Fit = 'fit',
  Fill = 'fill',
  Stretch = 'stretch',
  Tile = 'tile',
}

export enum BorderPosition {
  Center = 'center',
  Inside = 'inside',
  Outside = 'outside',
}

export type EnablableSection = {
  isEnabled: boolean
}

export interface Border extends EnablableSection {
  fillType: FillType
  color?: Color
  gradient?: Gradient
  position: BorderPosition
  thickness: number
  appearance: Appearance
  blendMode?: string
}

export interface BorderOptions {
  dashPattern: number[] | null
  isEnabled: boolean
  lineCapStyle: 'butt' | 'round' | 'square'
  lineJoinStyle: 'miter' | 'round' | 'bevel'
}

export interface Fill extends EnablableSection {
  type: FillType
  color?: Color
  gradient?: Gradient
  patternFillType?: ImageFillType
  patternTileScale?: number
  appearance: Appearance
  noiseIndex?: number
  noiseIntensity?: number
  blendMode?: string
}

export enum TextAlignment {
  Left = 'left',
  Right = 'right',
  Top = 'top',
  Bottom = 'bottom',
  Center = 'center',
  Justified = 'justified',
  Middle = 'middle',
}
export type HorizontalTextAlignment = Exclude<
  TextAlignment,
  TextAlignment.Top | TextAlignment.Middle | TextAlignment.Bottom
>

export type VerticalTextAlignment = Exclude<
  TextAlignment,
  | TextAlignment.Left
  | TextAlignment.Center
  | TextAlignment.Right
  | TextAlignment.Justified
>
export interface ParagraphStyle {
  spacing?: number
  minLineHeight?: number
  maxLineHeight?: number
  alignment?: HorizontalTextAlignment
}

export interface Font {
  family?: string
  weight?: string
  weightName?: string
  size?: number
  style?: string
  originalValue?: string
}

export interface TextAttributes {
  font?: Font
  color?: Color
  letterSpacing?: number
  paragraphStyle?: ParagraphStyle
  verticalAlignment?: VerticalTextAlignment
}

export interface TextPartialString {
  location: number
  length: number
  attributes: TextAttributes
}

export interface TextAttributedString {
  string: string
  attributes: TextPartialString[]
}

export interface TextProps {
  attributedString: TextAttributedString
  style?: Style
}

export interface TextStyleProps {
  verticalAlignment?:
    | TextAlignment.Top
    | TextAlignment.Middle
    | TextAlignment.Bottom
}

export interface TextStyle {
  text?: TextAttributes | null
}
